import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Loader } from '@calendly/ui/components/loader'
import { MainPanel } from '@calendly/booking-kit'
import { TitleText } from '@calendly/ui/components/typography'
import { useFlag } from '@calendly/feature-check-react'

import { useI18n } from 'locales'
import LayoutContainer from 'components/layout/container'
import {
  eventTypeSelector,
  setInvitee,
  useConfirmInviteeMutation,
  useGetEventTypeQuery
} from 'root-api'
import { useSchedulingLink } from 'components/use_scheduling_link'
import ErrorAlert from 'components/error/alert'
import { eventSelector } from 'selectors'
import ErrorPage from 'components/error/page'
import { currentUserSelector } from 'selectors/user'

import {
  ConfirmationDetails,
  Container,
  ConfirmationFooter,
  ConfirmationHeader,
  CookieSettingsButton
} from '../layout'

export const ReconfirmationPage = () => {
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const schedulingLink = useSchedulingLink()
  const event = useSelector(eventSelector)
  const eventType = useSelector(eventTypeSelector)
  const user = useSelector(currentUserSelector) || {}
  const bookingAppRedesign = useFlag('booking_app_redesign', false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [
    confirmInvitee,
    { data: inviteeConfirmation, isError: isConfirmError }
  ] = useConfirmInviteeMutation()

  const { isError: isEventTypeError } = useGetEventTypeQuery(
    { shareUuid: inviteeConfirmation?.scheduling_link?.owner_uuid },
    { skip: !inviteeConfirmation || inviteeConfirmation?.unavailability_reason }
  )

  useEffect(() => {
    if (!hasSubmitted) {
      const fetchConfirmation = async () => {
        try {
          const result = await confirmInvitee({ uuid: schedulingLink.uid }).unwrap()
          if (!result.unavailability_reason) dispatch(setInvitee(result))
          setHasSubmitted(true)
        } catch {
          // noop
        }
      }

      fetchConfirmation()
    }
  }, [hasSubmitted, confirmInvitee, schedulingLink, dispatch])

  if (isConfirmError || isEventTypeError) {
    return (
      <ErrorAlert
        error={{}}
        buttonClick={() => window.location.reload()}
      />
    )
  }

  if (inviteeConfirmation?.unavailability_reason) {
    return (
      <ErrorPage
        current_user={user}
        error={inviteeConfirmation.unavailability_reason}
      />
    )
  }

  if (!event || !eventType) return <Loader />

  return (
    <LayoutContainer expanded hideBranding={bookingAppRedesign}>
      <MainPanel>
        <Container>
          <ConfirmationHeader>
            <TitleText level={2} size="large" as="h1">
              {i18n.t('booking.templates.reconfirmation_title')}
            </TitleText>
          </ConfirmationHeader>
          <ConfirmationDetails />
          <ConfirmationFooter isReconfirmation />
          <CookieSettingsButton />
        </Container>
      </MainPanel>
    </LayoutContainer>
  )
}
